import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

interface ThreadCreateButtonProps {
  /** Viewport coordinates just above the selected text. */
  position: { top: number; left: number };
  label: string;
  creating: boolean;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * Fixed action shown while text in an assistant response is selected.
 *
 * Preventing the pointer-down default is important: otherwise the browser
 * clears the text selection before the click can create its thread.
 */
export function ThreadCreateButton({
  position,
  label,
  creating,
  disabled = false,
  onClick,
}: ThreadCreateButtonProps) {
  const preserveSelection = (event: React.MouseEvent<HTMLButtonElement> | React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onPointerDown={preserveSelection}
      onMouseDown={preserveSelection}
      onClick={onClick}
      disabled={creating || disabled}
      aria-label={label}
      aria-busy={creating}
      data-thread-create-button
      data-testid="highlight-thread-create-button"
      className="fixed z-[70] h-auto -translate-x-1/2 -translate-y-full gap-1.5 bg-background/95 px-2.5 py-1.5 text-xs text-muted-foreground backdrop-blur hover:bg-muted hover:text-foreground"
      style={{ top: position.top, left: position.left }}
    >
      {creating ? <Spinner className="size-3" /> : <MessageSquare className="size-3.5" />}
      {label}
    </Button>
  );
}
